/**
 * WebhookMonitor — dev-facing Booking Debug & XML Monitor.
 *
 * Renders below the existing Bookings table with zero coupling to the
 * rest of the Bookings module. Consumes ONLY read-only endpoints:
 *   GET /api/mmt/booking-debug          (list, paginated)
 *   GET /api/mmt/booking-debug/{id}     (full record with XMLs)
 *   POST /api/bookings/{id}/refetch     (existing "Fetch Again" endpoint)
 *
 * Never touches auth, tokens, ARI, booking calculations, or the bookings
 * table itself.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Copy, Download, Eye, RefreshCw, RotateCcw, ChevronDown, ChevronRight,
  Activity, AlertCircle, CheckCircle2, Clock,
} from "lucide-react";

import { Card, CardHeader, CardTitle, CardContent } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Badge } from "./components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./components/ui/tabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_BADGE = {
  received:          { label: "Received",          cls: "bg-blue-100 text-blue-800" },
  listing_fetch:     { label: "Listing Fetch",     cls: "bg-cyan-100 text-cyan-800" },
  listing_matched:   { label: "Listing Matched",   cls: "bg-teal-100 text-teal-800" },
  listing_failed:    { label: "Listing Failed",    cls: "bg-orange-100 text-orange-800" },
  listing_fallback:  { label: "Listing Fallback",  cls: "bg-amber-100 text-amber-800" },
  detail_fetch:      { label: "Detail Fetch",      cls: "bg-yellow-100 text-yellow-800" },
  response_received: { label: "Response Received", cls: "bg-purple-100 text-purple-800" },
  saved:             { label: "Saved",             cls: "bg-emerald-100 text-emerald-800" },
  error:             { label: "Error",             cls: "bg-red-100 text-red-800" },
};

const EVENT_LABEL = {
  new_booking:  "Booked",
  cancellation: "Cancelled",
  modification: "Modified",
  unknown:      "Unknown",
};

function ProcessingBadge({ status }) {
  const s = STATUS_BADGE[status] || { label: status || "-", cls: "bg-gray-100 text-gray-800" };
  return <Badge className={s.cls} data-testid={`proc-status-${status}`}>{s.label}</Badge>;
}

function EventBadge({ event_type }) {
  const label = EVENT_LABEL[event_type] || event_type || "-";
  const cls =
    event_type === "cancellation"
      ? "bg-red-50 text-red-700 border border-red-200"
      : event_type === "modification"
      ? "bg-amber-50 text-amber-700 border border-amber-200"
      : event_type === "new_booking"
      ? "bg-green-50 text-green-700 border border-green-200"
      : "bg-slate-50 text-slate-700 border border-slate-200";
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${cls}`}>{label}</span>;
}

function OtaBadge({ ota }) {
  const key = (ota || "").toLowerCase();
  const cls =
    key === "agoda"
      ? "bg-rose-100 text-rose-800 border border-rose-200"
      : key === "mmt"
      ? "bg-indigo-100 text-indigo-800 border border-indigo-200"
      : "bg-slate-100 text-slate-700 border border-slate-200";
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${cls}`} data-testid={`ota-badge-${key}`}>
      {ota || "-"}
    </span>
  );
}

function JsonViewer({ label, data, filenameHint }) {
  const text = data == null ? "" : JSON.stringify(data, null, 2);
  const empty = !text || !text.trim() || text === "{}";
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard`);
    } catch {
      toast.error("Copy failed");
    }
  };
  const onDownload = () => {
    const blob = new Blob([text], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filenameHint || `${label.replace(/\s+/g, "_")}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="space-y-2" data-testid={`json-viewer-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={onCopy} disabled={empty} data-testid="json-copy-btn">
            <Copy className="w-3.5 h-3.5 mr-1" /> Copy
          </Button>
          <Button size="sm" variant="outline" onClick={onDownload} disabled={empty} data-testid="json-download-btn">
            <Download className="w-3.5 h-3.5 mr-1" /> Download
          </Button>
        </div>
      </div>
      <pre className="bg-slate-900 text-slate-100 text-xs p-3 rounded overflow-auto max-h-[420px] whitespace-pre-wrap break-all font-mono">
        {empty ? <span className="text-slate-400 italic">No parsed data captured for this event.</span> : text}
      </pre>
    </div>
  );
}

function formatXml(xml) {
  if (!xml) return "";
  try {
    // Very light pretty-printer for readability. Idempotent — no data mutation.
    const PADDING = "  ";
    const reg = /(>)(<)(\/*)/g;
    const withNewlines = xml.replace(reg, "$1\n$2$3");
    let pad = 0;
    return withNewlines.split("\n").map((node) => {
      let indent = 0;
      if (/.+<\/\w[^>]*>$/.test(node)) indent = 0;
      else if (/^<\/\w/.test(node)) pad = Math.max(pad - 1, 0);
      else if (/^<\w[^>]*[^/]>.*$/.test(node)) indent = 1;
      else indent = 0;
      const line = PADDING.repeat(pad) + node;
      pad += indent;
      return line;
    }).join("\n");
  } catch {
    return xml;
  }
}

function XmlViewer({ label, xml, filenameHint }) {
  const empty = !xml || !xml.trim();
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(xml || "");
      toast.success(`${label} copied to clipboard`);
    } catch {
      toast.error("Copy failed");
    }
  };
  const onDownload = () => {
    const blob = new Blob([xml || ""], { type: "application/xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filenameHint || `${label.replace(/\s+/g, "_")}.xml`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="space-y-2" data-testid={`xml-viewer-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={onCopy} disabled={empty} data-testid="xml-copy-btn">
            <Copy className="w-3.5 h-3.5 mr-1" /> Copy
          </Button>
          <Button size="sm" variant="outline" onClick={onDownload} disabled={empty} data-testid="xml-download-btn">
            <Download className="w-3.5 h-3.5 mr-1" /> Download
          </Button>
        </div>
      </div>
      <pre className="bg-slate-900 text-slate-100 text-xs p-3 rounded overflow-auto max-h-[420px] whitespace-pre-wrap break-all font-mono">
        {empty ? <span className="text-slate-400 italic">Not yet captured for this event.</span>
               : formatXml(xml)}
      </pre>
    </div>
  );
}

function Timeline({ steps }) {
  if (!steps || steps.length === 0) return <div className="text-xs text-gray-500">No events yet.</div>;
  return (
    <ol className="space-y-1.5">
      {steps.map((s, i) => {
        const isErr = s.step === "error" || s.step === "listing_failed";
        const isSaved = s.step === "booking_saved";
        const isMatched = s.step === "listing_matched";
        const isFallback = s.step === "listing_fallback";
        const Icon = isErr ? AlertCircle : (isSaved || isMatched) ? CheckCircle2 : Clock;
        const color =
          isErr ? "text-red-600" :
          isSaved ? "text-emerald-600" :
          isMatched ? "text-teal-600" :
          isFallback ? "text-amber-600" :
          "text-slate-600";
        return (
          <li key={i} className="flex items-start gap-2 text-xs">
            <Icon className={`w-3.5 h-3.5 mt-0.5 ${color}`} />
            <div className="flex-1">
              <div className="font-mono text-slate-700">
                <span className="text-slate-400">[{String(s.elapsed_ms ?? 0).padStart(5)} ms]</span>{" "}
                <span className={`font-semibold ${color}`}>{s.step}</span>
              </div>
              {s.note && <div className="text-slate-500 ml-1">{s.note}</div>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export default function WebhookMonitor({ propertyId }) {
  const [open, setOpen] = useState(false);
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);   // full record for the modal
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchList = useCallback(async () => {
    // Fail-closed: with no property selected, show nothing (per-property scoping contract).
    if (!propertyId) {
      setRecords([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/webhook-monitor`, {
        params: { limit: 50, property_id: propertyId },
      });
      setRecords(data.records || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error("Failed to load webhook monitor");
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  // Auto-refresh whenever the selected property changes (even when collapsed
  // — keeps the count badge honest).
  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // Poll every 20s while expanded so the user sees near-live updates.
  useEffect(() => {
    if (!open) return;
    const t = setInterval(fetchList, 20000);
    return () => clearInterval(t);
  }, [open, fetchList]);

  const openDetail = async (id) => {
    setDetail({ id });         // show modal skeleton immediately
    setDetailLoading(true);
    try {
      const { data } = await axios.get(`${API}/webhook-monitor/${id}`);
      setDetail(data);
    } catch {
      toast.error("Failed to load debug record");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const refetch = async (record) => {
    const bookingId = record?.booking_id;
    if (!bookingId) return;
    const ota = (record?.ota || "").toLowerCase();
    try {
      toast.loading("Re-fetching booking detail…", { id: `refetch-${bookingId}` });
      if (ota === "agoda") {
        await axios.post(`${API}/agoda/backfill-booking-details`, null, {
          params: { booking_id: bookingId },
        });
      } else {
        await axios.post(`${API}/bookings/${bookingId}/refetch`);
      }
      toast.success("Booking detail re-fetched", { id: `refetch-${bookingId}` });
      // Give the backend a moment then refresh the monitor list.
      setTimeout(fetchList, 800);
    } catch (e) {
      toast.error(e.response?.data?.detail?.error || e.response?.data?.detail || "Re-fetch failed",
                  { id: `refetch-${bookingId}` });
    }
  };

  return (
    <Card className="mt-6" data-testid="webhook-monitor-card">
      <CardHeader className="cursor-pointer" onClick={() => setOpen(!open)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            <Activity className="w-5 h-5 text-indigo-600" />
            <CardTitle className="text-base">Webhook Monitor</CardTitle>
            <Badge className="bg-slate-100 text-slate-700" data-testid="webhook-monitor-count">
              {total} event{total === 1 ? "" : "s"}
            </Badge>
          </div>
          {open && (
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => { e.stopPropagation(); fetchList(); }}
              data-testid="webhook-monitor-refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          )}
        </div>
        {!open && (
          <div className="text-xs text-gray-500 mt-1 ml-6">
            Developer view — every MMT &amp; Agoda booking webhook with raw payload, parsed data, request/response and processing timeline
          </div>
        )}
      </CardHeader>
      {open && (
        <CardContent>
          {loading && records.length === 0 ? (
            <div className="text-sm text-gray-500 text-center py-6">Loading…</div>
          ) : records.length === 0 ? (
            <div className="text-sm text-gray-500 text-center py-6" data-testid="webhook-monitor-empty">
              {propertyId
                ? "No webhook notifications received yet."
                : "Select a property to view its webhook events."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm" data-testid="webhook-monitor-table">
                <thead className="text-left border-b border-gray-200">
                  <tr>
                    <th className="py-2 pr-3 font-medium text-gray-600">Source</th>
                    <th className="py-2 pr-3 font-medium text-gray-600">Received</th>
                    <th className="py-2 pr-3 font-medium text-gray-600">Hotel Code</th>
                    <th className="py-2 pr-3 font-medium text-gray-600">Booking ID</th>
                    <th className="py-2 pr-3 font-medium text-gray-600">Event</th>
                    <th className="py-2 pr-3 font-medium text-gray-600">Status</th>
                    <th className="py-2 pr-3 font-medium text-gray-600">Duration</th>
                    <th className="py-2 pr-3 font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((r) => (
                    <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`webhook-row-${r.booking_id}`}>
                      <td className="py-2 pr-3"><OtaBadge ota={r.ota} /></td>
                      <td className="py-2 pr-3 text-xs text-gray-700 font-mono">
                        {r.received_at ? new Date(r.received_at).toLocaleString() : "-"}
                      </td>
                      <td className="py-2 pr-3 font-mono text-xs">{r.hotel_code || "-"}</td>
                      <td className="py-2 pr-3 font-mono text-xs">{r.booking_id || "-"}</td>
                      <td className="py-2 pr-3"><EventBadge event_type={r.event_type} /></td>
                      <td className="py-2 pr-3"><ProcessingBadge status={r.processing_status} /></td>
                      <td className="py-2 pr-3 text-xs text-gray-600 font-mono">
                        {r.processing_ms ? `${r.processing_ms} ms` : "-"}
                      </td>
                      <td className="py-2 pr-3">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openDetail(r.id)}
                            data-testid={`view-xml-btn-${r.booking_id}`}
                          >
                            <Eye className="w-3.5 h-3.5 mr-1" /> View
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => refetch(r)}
                            disabled={!r.booking_id}
                            data-testid={`refetch-btn-${r.booking_id}`}
                          >
                            <RotateCcw className="w-3.5 h-3.5 mr-1" /> Fetch Again
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      )}

      {/* Detail modal — full XMLs + timeline */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="webhook-detail-modal">
          <DialogHeader>
            <DialogTitle>
              <span className="inline-flex items-center gap-2">
                {detail?.ota && <OtaBadge ota={detail.ota} />}
                Webhook Debug — {detail?.booking_id || "…"}
              </span>
              {detail?.hotel_code && (
                <span className="ml-2 text-xs font-mono text-gray-500">hotel_code={detail.hotel_code}</span>
              )}
            </DialogTitle>
          </DialogHeader>
          {detailLoading && !detail?.timeline ? (
            <div className="text-sm text-gray-500 py-8 text-center">Loading debug record…</div>
          ) : detail?.timeline ? (
            (() => {
            const isAgoda = (detail?.ota || "").toLowerCase() === "agoda";
            return (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <div><div className="text-gray-500">Source</div><OtaBadge ota={detail.ota} /></div>
                <div><div className="text-gray-500">Event</div><EventBadge event_type={detail.event_type} /></div>
                <div><div className="text-gray-500">Status</div><ProcessingBadge status={detail.processing_status} /></div>
                <div><div className="text-gray-500">Total time</div><div className="font-mono">{detail.processing_ms ?? "-"} ms</div></div>
                <div><div className="text-gray-500">Detail HTTP</div><div className="font-mono" data-testid="detail-http-status">{detail.detail_http_status ?? "-"}</div></div>
                {!isAgoda && (<>
                  <div><div className="text-gray-500">Listing HTTP</div><div className="font-mono" data-testid="listing-http-status">{detail.listing_http_status ?? "-"}</div></div>
                  <div><div className="text-gray-500">Listing Attempts</div><div className="font-mono" data-testid="listing-attempts">{detail.listing_attempts ?? "-"}</div></div>
                  <div><div className="text-gray-500">Listing Matched</div>
                    <div className="font-mono" data-testid="listing-matched">
                      {detail.listing_matched === true ? "yes" :
                       detail.listing_matched === false ? "no" : "-"}
                    </div>
                  </div>
                </>)}
              </div>
              {detail.error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800" data-testid="webhook-detail-error">
                  <AlertCircle className="w-4 h-4 inline mr-1" /> {detail.error}
                </div>
              )}
              <div className="border rounded p-3 bg-slate-50">
                <div className="text-sm font-medium mb-2 text-gray-700">Processing Timeline</div>
                <Timeline steps={detail.timeline} />
              </div>

              {isAgoda ? (
                <Tabs defaultValue="notification">
                  <TabsList className="grid grid-cols-4 w-full">
                    <TabsTrigger value="notification" data-testid="xml-tab-notification">Hint XML</TabsTrigger>
                    <TabsTrigger value="parsed" data-testid="tab-parsed-data">Parsed Data</TabsTrigger>
                    <TabsTrigger value="request" data-testid="xml-tab-request">Detail Request</TabsTrigger>
                    <TabsTrigger value="response" data-testid="xml-tab-response">Detail Response</TabsTrigger>
                  </TabsList>
                  <TabsContent value="notification" className="mt-3">
                    <XmlViewer
                      label="Booking Hint XML (received)"
                      xml={detail.notification_xml}
                      filenameHint={`agoda_hint_${detail.booking_id || "unknown"}.xml`}
                    />
                  </TabsContent>
                  <TabsContent value="parsed" className="mt-3">
                    <JsonViewer
                      label="Parsed Booking Data"
                      data={detail.parsed_data}
                      filenameHint={`agoda_parsed_${detail.booking_id || "unknown"}.json`}
                    />
                  </TabsContent>
                  <TabsContent value="request" className="mt-3">
                    <XmlViewer
                      label="GetBookingDetail Request XML"
                      xml={detail.detail_request_xml}
                      filenameHint={`agoda_request_${detail.booking_id || "unknown"}.xml`}
                    />
                  </TabsContent>
                  <TabsContent value="response" className="mt-3">
                    <XmlViewer
                      label="GetBookingDetail Response XML"
                      xml={detail.detail_response_xml}
                      filenameHint={`agoda_response_${detail.booking_id || "unknown"}.xml`}
                    />
                  </TabsContent>
                </Tabs>
              ) : (
                <Tabs defaultValue="notification">
                  <TabsList className="grid grid-cols-5 w-full">
                    <TabsTrigger value="notification" data-testid="xml-tab-notification">Notification</TabsTrigger>
                    <TabsTrigger value="listing-request" data-testid="xml-tab-listing-request">Listing Request</TabsTrigger>
                    <TabsTrigger value="listing-response" data-testid="xml-tab-listing-response">Listing Response</TabsTrigger>
                    <TabsTrigger value="request" data-testid="xml-tab-request">Detail Request</TabsTrigger>
                    <TabsTrigger value="response" data-testid="xml-tab-response">Detail Response</TabsTrigger>
                  </TabsList>
                  <TabsContent value="notification" className="mt-3">
                    <XmlViewer
                      label="Notification XML"
                      xml={detail.notification_xml}
                      filenameHint={`notification_${detail.booking_id || "unknown"}.xml`}
                    />
                  </TabsContent>
                  <TabsContent value="listing-request" className="mt-3">
                    <XmlViewer
                      label="Listing Request XML"
                      xml={detail.listing_request_xml}
                      filenameHint={`listing_request_${detail.booking_id || "unknown"}.xml`}
                    />
                  </TabsContent>
                  <TabsContent value="listing-response" className="mt-3">
                    <XmlViewer
                      label="Listing Response XML"
                      xml={detail.listing_response_xml}
                      filenameHint={`listing_response_${detail.booking_id || "unknown"}.xml`}
                    />
                  </TabsContent>
                  <TabsContent value="request" className="mt-3">
                    <XmlViewer
                      label="Detail Request XML"
                      xml={detail.detail_request_xml}
                      filenameHint={`request_${detail.booking_id || "unknown"}.xml`}
                    />
                  </TabsContent>
                  <TabsContent value="response" className="mt-3">
                    <XmlViewer
                      label="Detail Response XML"
                      xml={detail.detail_response_xml}
                      filenameHint={`response_${detail.booking_id || "unknown"}.xml`}
                    />
                  </TabsContent>
                </Tabs>
              )}

              <div className="flex justify-end pt-2 gap-2">
                <Button
                  variant="outline"
                  onClick={() => refetch(detail)}
                  disabled={!detail.booking_id}
                  data-testid="webhook-detail-refetch-btn"
                >
                  <RotateCcw className="w-4 h-4 mr-1" /> Fetch Booking Detail Again
                </Button>
              </div>
            </div>
            );
            })()
          ) : null}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
